<template>
  <div>
    <ts-page-title
      :title="$t('employeeCardActivator.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('employeeCardActivator.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper class="ts-flex ts-items-center ts-justify-between">
        <div class="ts-flex ts-justify-between ts-space-x-2">
          <div class="row">
            <div class="col-lg-5">
              <label class="form-col-label control-label ts-mb-1 required">{{
                $t('employeeCardActivator.employee')
              }}</label>
              <Select
                v-model="model.employee_id"
                clearable
                :class="{
                  'ivu-form-item-error': errors.has('employee_id')
                }"
                :filterable="true"
                :filter-by-label="true"
                :clearable="true"
              >
                <Option
                  v-for="(employee, index) in employees"
                  :key="index"
                  :value="employee.employee_id"
                  :label="
                    employee.card_id
                      ? employee.card_id + '-' + employee.employee_name_en
                      : employee.employee_name_en
                  "
                  >{{
                    employee.card_id
                      ? employee.card_id + '-' + employee.employee_name_en
                      : employee.employee_name_en
                  }}
                </Option>
              </Select>
              <div
                class="ivu-form-item-error ts-text-red-600"
                v-if="errors.has('employee_id')"
              >
                {{ errors.first('employee_id') }}
              </div>
            </div>
            <div class="col-lg-5">
              <label class="form-col-label control-label ts-mb-1 required">{{
                $t('employeeCardActivator.serialCard')
              }}</label>
              <input
                type="text"
                v-model="model.serial_card"
                class="form-control"
                :placeholder="$t('employeeCardActivator.enterSerialCard')"
                :class="{
                  'is-invalid': errors.has('serial_card')
                }"
              />
              <div class="ts-text-red-600" v-if="errors.has('serial_card')">
                {{ errors.first('serial_card') }}
              </div>
            </div>
            <div class="col-lg-2 ts-pt-5">
              <ts-button
                color="primary"
                :disabled="saving"
                :waiting="saving"
                @click="save"
              >
                {{ $t('save') }}
              </ts-button>
            </div>
          </div>
        </div>
        <div class="ts-justify-end ts-space-x-2">
          <Input
            search
            v-model="search"
            :placeholder="$t('employee.search')"
            :style="width"
            @on-focus="() => (width = 'width: 500px')"
            @on-blur="() => (width = 'width: 300px')"
          />
        </div>
      </ts-panel-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        max-height="700"
        highlight-row
        stripe
        border
      >
        <template slot-scope="{ row }" slot="branch">
          {{
            row.branch
              ? locale === 'kh'
                ? row.branch.branch_name_kh
                  ? row.branch.branch_name_kh
                  : row.branch.branch_name_en
                : row.branch.branch_name_en
              : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="employee_name">
          <strong>{{
            row.employee
              ? locale === 'kh'
                ? row.employee.employee_name_kh
                  ? row.employee.employee_name_kh
                  : row.employee.employee_name_en
                : row.employee.employee_name_en
              : ''
          }}</strong>
          {{ row.card_id ? '(' + row.card_id + ')' : '' }}
        </template>
        <template slot-scope="{ row }" slot="position">
          {{
            row.employee
              ? row.employee.logical_position
                ? locale === 'kh'
                  ? row.employee.logical_position.position_name_kh
                    ? row.employee.logical_position.position_name_kh
                    : row.employee.logical_position.position_name_en
                  : row.employee.logical_position.position_name_en
                : ''
              : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="serial_card">
          <span
            class="ts-cursor-pointer text-primary ts-font-bold"
            v-clipboard:copy="row.serial_card"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
            v-tooltip="copied"
            >{{ row.serial_card }}
          </span>
        </template>
        <template slot-scope="{ row }" slot="activated_by">
          {{
            row.activated_by
              ? row.activated_by.employee
                ? row.activated_by.employee.employee_name_en
                : row.activated_by.user_name
              : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="activated_by">
          {{
            row.activated_by
              ? row.activated_by.employee
                ? row.activated_by.employee.employee_name_en
                : row.activated_by.user_name
              : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="status">
          <span
            class="badge"
            :class="{
              'badge-success': row.status_id === 1,
              'badge-danger': row.status_id === 2
            }"
            >{{ row.card_status ? row.card_status.card_status : '' }}</span
          >
        </template>
        <template slot-scope="{ row }" slot="status_changed_by">
          {{
            row.status_changed_by
              ? row.status_changed_by.employee
                ? row.status_changed_by.employee.employee_name_en
                : row.status_changed_by.user_name
              : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <a
            class="text-warning"
            v-tooltip="$t('employeeCardActivator.changeStatus')"
            @click.prevent="showForm(row, false)"
            :disabled="row._deleting"
          >
            <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
            <i class="fas fa-random" v-else></i>
          </a>
        </template>
      </Table>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
    </ts-panel>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce } from 'lodash'
import FormComponent from './confirm-deactivate-old-card.vue'

export default {
  name: 'cardActivator',
  components: {
    FormComponent
  },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      saving: false,
      width: 'width: 300px',
      copied: 'Click to copy serial',
      model: {
        serial_card: '',
        employee_id: null
      },
      status_id: null
    }
  },
  computed: {
    ...mapState('humanResource/cardActivator', [
      'resources',
      'pagination',
      'employees'
    ]),
    locale() {
      return this.$root.$i18n.locale
    },
    search: {
      get() {
        return this.$store.state.humanResource.cardActivator.search
      },
      set(newValue) {
        this.$store.commit('humanResource/cardActivator/SET_SEARCH', newValue)
        this.$store.commit('humanResource/cardActivator/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'index',
          width: 60,
          align: 'center'
        },
        {
          title: this.$t('employeeCardActivator.branch'),
          slot: 'branch',
          minWidth: 150
        },
        {
          title: this.$t('employeeCardActivator.employee'),
          slot: 'employee_name',
          minWidth: 200
        },
        {
          title: this.$t('employeeCardActivator.position'),
          slot: 'position',
          minWidth: 200
        },
        {
          title: this.$t('employeeCardActivator.serialCard'),
          slot: 'serial_card',
          minWidth: 120,
          align: 'center'
        },
        {
          title: this.$t('employeeCardActivator.activatedBy'),
          slot: 'activated_by',
          minWidth: 150
        },
        {
          title: this.$t('employeeCardActivator.activatedDate'),
          key: 'activated_date',
          minWidth: 200,
          align: 'center'
        },
        {
          title: this.$t('employeeCardActivator.status'),
          slot: 'status',
          minWidth: 100,
          align: 'center',
          fixed: 'right',
          filters: [
            {
              label: this.$t('employeeCardActivator.active'),
              value: 1
            },
            {
              label: this.$t('employeeCardActivator.inactive'),
              value: 2
            }
          ],
          filterMultiple: false,
          filterRemote(value) {
            this.status_id = value
            this.fetchData()
          }
        },
        {
          title: this.$t('employeeCardActivator.statusReason'),
          key: 'status_reason',
          minWidth: 300
        },
        {
          title: this.$t('employeeCardActivator.statusChangeBy'),
          slot: 'status_changed_by',
          minWidth: 200
        },
        {
          title: this.$t('employeeCardActivator.statusChangeDate'),
          key: 'status_change_date',
          minWidth: 200,
          align: 'center'
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          minWidth: 100,
          align: 'center',
          fixed: 'right'
        }
      ]
    }
  },
  methods: {
    ...mapActions('humanResource/cardActivator', ['getEmployee']),
    async fetchResource() {
      this.loading = true
      await this.getEmployee()
      this.loading = false
    },
    async fetchData(attributes) {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('humanResource/cardActivator/fetch', {
          status_id: this.status_id,
          ...attributes
        })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
    },
    onCopy: function(e) {
      this.copied = 'Copied'
      setTimeout(() => {
        this.copied = 'Click to copy serial'
      }, 1000)
    },
    onError: function(e) {
      this.copied = 'Failed Copied'
    },
    async checkEmployeeCardStatus(data) {
      this.errors = new Errors()
      try {
        let response = await this.$store.dispatch(
          'humanResource/cardActivator/checkCurrentEmployeeCardStatus',
          data
        )
        if (response.status == true) {
          this.showForm(data, true)
        }

        return response.status
      } catch (error) {
        this.errors = new Errors(error.errors)
        this.$toasted.error(error.message)
      }
    },

    showForm(data, create_new_card) {
      this.$modal.show(
        FormComponent,
        {
          data: data,
          create_new_card: create_new_card
        },
        {
          height: 'auto',
          width: '330px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        },
        {
          'before-close': this.clearForm
        }
      )
    },

    async save() {
      this.saving = true
      this.errors = new Errors()
      this.check = await this.checkEmployeeCardStatus(this.model)
      if (this.check == false) {
        try {
          let response = await this.$store.dispatch(
            'humanResource/cardActivator/store',
            this.model
          )
          if (response.message_id > 0) {
            this.notice({ type: 'warning', text: response.message })
          }
          if (response.message_id == 0) {
            this.notice({ type: 'success', text: response.message })
            this.clearForm()
            this.fetchData()
          }
          this.saving = false
        } catch (error) {
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        }
        this.saving = false
      }
      this.saving = false
    },

    clearForm() {
      this.model = {
        employee_id: null,
        serial_card: ''
      }
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'CARD ACTIVATOR',
        desc: not.text
      })
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('humanResource/cardActivator/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchResource()
      vm.fetchData()
    })
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  }
}
</script>
